import { useState, type ReactNode } from 'react';
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import { Users } from 'lucide-react';
import type { SeatingParty } from '@peles/shared';
import { guestOfDragId } from './dropTargets';

/**
 * The drag and drop of parties onto tables and seats, with the party's name
 * following the pointer — the list it is dragged out of scrolls and clips, so
 * the chip itself cannot. A press still taps (a drag starts after 6px).
 */
export function SeatingDnd({
  parties,
  onDragEnd,
  children,
}: {
  parties: Map<string, SeatingParty>;
  onDragEnd: (event: DragEndEvent) => void;
  children: ReactNode;
}) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor),
  );
  const [dragging, setDragging] = useState<string | null>(null);
  const party = dragging ? parties.get(dragging) : undefined;
  return (
    <DndContext
      sensors={sensors}
      onDragStart={(event) => setDragging(guestOfDragId(event.active.id))}
      onDragCancel={() => setDragging(null)}
      onDragEnd={(event) => {
        setDragging(null);
        onDragEnd(event);
      }}
    >
      {children}
      <DragOverlay dropAnimation={null}>
        {party ? (
          <div
            className="flex items-center gap-1.5 rounded-lg border border-primary-400 bg-white px-2 py-1.5 text-sm font-medium text-gray-900 shadow-lg"
            data-testid="seating-drag-overlay"
          >
            <Users size={14} className="text-primary-600" />
            {party.name}
            <span className="text-xs text-gray-500">×{party.partySize}</span>
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}
